import React, { useState, useEffect } from "react";
import { hashHistory } from "react-router";
import Grid from "@material-ui/core/Grid";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import TableInc from "../../../shared-ui/TableInc";
import ButtonInc from "../../../shared-ui/ButtonInc";
import { formatDateLocale } from "../../../lib/utils.es6";
import { getPartnerGuid } from "../../../lib/access.es6";
import { PARTNERS_GUID_ENUM } from "../../../routes/PartnersEnum";
import { useProductCurrencies } from "../../../hooks/useProductCurrency";
import {
  isFirstStepPending,
  isAmlStagePending,
  isPendingStage,
  isPendingNonScreeningStage,
  isStageDecision,
  isCorrectedForStage,
  getStageCheck,
  stageLabel,
} from "./makerCheckStage";

// What a stage descriptor matches. `match` names the tab it backs: the plain
// queue (default), the corrected work waiting on that same stage, or what the
// stage decided.
//
// The plain queue holds corrected policies unless `excludeCorrected` says a
// Corrected tab is catching them — a returned policy clears its need_action
// flag but keeps the corrected_for_stage stamp, so excluding it without that
// tab leaves it in no tab at all: out of Awaiting Approval by this predicate,
// out of Need Action by the flag, and undecided so out of both decision tabs.
function stagePredicate(stage) {
  if (stage.match === "Approved" || stage.match === "Rejected") {
    return function (policy) {
      return isStageDecision(policy, stage, stage.match);
    };
  }
  if (stage.match === "corrected") {
    return function (policy) {
      return isPendingStage(policy, stage) && isCorrectedForStage(policy, stage);
    };
  }
  return function (policy) {
    if (!isPendingStage(policy, stage)) {
      return false;
    }
    return !(stage.excludeCorrected && isCorrectedForStage(policy, stage));
  };
}

// Named maker-stage partitions the OMM dashboards ask for by string. A caller
// can also pass a stage descriptor object ({ label, sequenceIndex }) to filter
// down to policies sitting on exactly that approval stage.
const MAKER_STAGE_FILTERS = {
  // Sales Manager: policies awaiting the FIRST maker-checker step.
  firstStepOnly: isFirstStepPending,
  // New Business sees every policy — AML-flagged or not — while it is still
  // moving through Sales Approval and Quality Check. Once BOTH of those are
  // approved the policy reaches its AML stage (AML Check / AML approval); from
  // that point it belongs to the AML-flagged queue only, so it is dropped from
  // New Business here.
  excludeFirstStep: function (policy) {
    return !isAmlStagePending(policy);
  },
  // Any policy still inside its approval sequence, on a stage that does not
  // have a dashboard of its own. The Need Action basket is keyed on the
  // need_action flag rather than on a stage — a policy sent back for changes
  // still sits on the check of the level that sent it back — so this weeds out
  // policies that have left the sequence entirely, and those parked on an AML,
  // PEP or IPRS check, which the dedicated screening queues own.
  pendingApproval: isPendingNonScreeningStage,
};

const DisplayRecentPolicies = (props) => {
  // OMK has no agent codes: its embedded digital journeys stamp the acting
  // agent's name into additional_details.agent instead. So for OMK only, that
  // column slot carries the agent's name. Every other partner is untouched.
  const isOmk = getPartnerGuid() === PARTNERS_GUID_ENUM.OLDMUTUAL_KENYA;
  const isOmm = getPartnerGuid() === PARTNERS_GUID_ENUM.OLDMUTUAL_MALAWI;

  const [inputValue, setInputValue] = useState(props.searchValue || '')

  useEffect(function() {
    setInputValue(props.searchValue || '')
  }, [props.searchValue])

  // Start from the API page of policies, then apply any client-side tab filters.
  var basePolicies = props.policies || [];

  if (props.needAction === 'exclude') {
    basePolicies = basePolicies.filter(function(p) {
      var ad = p && p.additional_details;
      return !ad || ad.need_action !== true;
    });
  } else if (props.needAction === 'only') {
    basePolicies = basePolicies.filter(function(p) {
      var ad = p && p.additional_details;
      return !!ad && ad.need_action === true;
    });
  }

  // Partition by maker-checker stage — either a named partition or a stage
  // descriptor matching one specific approval level.
  if (props.makerStage) {
    var stageFilter =
      typeof props.makerStage === 'object'
        ? stagePredicate(props.makerStage)
        : MAKER_STAGE_FILTERS[props.makerStage];
    if (stageFilter) {
      basePolicies = basePolicies.filter(stageFilter);
    }
  }

  const sourcePolicies = basePolicies;

  // Resolve each policy's currency from its own product (falling back to the
  // partner's country currency) so premiums show the product currency, not the
  // partner country's — these can differ (e.g. OMK products priced in KES).
  const resolveCurrency = useProductCurrencies(
    sourcePolicies.map(function (policy) {
      return policy && policy.product_code;
    })
  );

  // Process policies data for table display
  // slice() first: without a client-side filter above, sourcePolicies IS the
  // array held in the store, and sort() would reorder it in place.
  const tempArr = sourcePolicies
    .slice()
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .map((policy) => ({
      ...policy,
      active: policy.active,
      created_at: formatDateLocale(policy.created_at) || "N/A",
      premium_granularity:
        policy.quotation && policy.quotation.total_premium_amount_in_cents
          ? `${resolveCurrency(policy.product_code)} ${policy.quotation.total_premium_amount_in_cents / 100} per ${policy.quotation.premium_cardinality} ${policy.quotation.premium_granularity}`
          : "N/A",
      agent_name:
        (policy && policy.additional_details && policy.additional_details.agent)
          ? policy.additional_details.agent
          : "N/A",
      agent_code:
        (policy && policy.additional_details && policy.additional_details.agent_code)
          ? policy.additional_details.agent_code
          : "N/A",
      agent_email:
        (policy && policy.additional_details && policy.additional_details.agent_email)
          ? policy.additional_details.agent_email
          : "N/A",
      market_segment:
        (policy && policy.additional_details && policy.additional_details.market_segment)
          ? policy.additional_details.market_segment
          : "N/A",
      agent_branch:
        (policy && policy.additional_details && policy.additional_details.agent_branch)
          ? policy.additional_details.agent_branch
          : "N/A",
      worksite_name:
        (policy && policy.additional_details && policy.additional_details.worksite_name)
          ? policy.additional_details.worksite_name
          : "N/A",
      principal: (() => {
        // Fail-safe: Check if policy exists
        if (!policy) return "N/A";

        // Find the principal from policy_schedule_entries
        if (policy.quotation && policy.quotation.policy_schedule_entries && Array.isArray(policy.quotation.policy_schedule_entries)) {
          const principalEntry = policy.quotation.policy_schedule_entries.find(
            entry => entry && entry.dependant && entry.dependant.relationship === "Principal"
          );
          if (principalEntry && principalEntry.dependant) {
            const { first_name, middle_name, last_name } = principalEntry.dependant;
            const fullName = [first_name, middle_name, last_name].filter(Boolean).join(" ");
            if (fullName.trim()) return fullName;
          }
        }

        return "N/A";
      })(),
    }));

  // Base columns that are always shown
  const baseColumns = [
    {
      title: "Policy Number",
      field: "policy_number",
      render: (rowData) => (
        <span style={{ fontFamily: 'monospace', color: '#2D3748', fontWeight: '500' }}>
          {rowData.policy_number || "N/A"}
        </span>
      ),
    },
  ];

  const agentCodeColumn = isOmk
    ? [
        {
          title: "Agent Name",
          field: "agent_name",
          render: (rowData) => {
            return (rowData && rowData.agent_name) ? rowData.agent_name : "N/A";
          },
        },
      ]
    : [
        {
          title: "Agent Code",
          field: "agent_code",
          render: (rowData) => {
            return (rowData && rowData.agent_code) ? rowData.agent_code : "N/A";
          },
        },
      ];

  const agentEmailColumn = [
    {
      title: "Agent Email",
      field: "agent_email",
      render: (rowData) => {
        return (rowData && rowData.agent_email) ? rowData.agent_email : "N/A";
      },
    },
  ];

  // OMK does not populate market_segment, so the column would only ever read
  // "N/A" there. Hidden for OMK; unchanged for every other partner.
  const marketSegmentColumn = isOmk
    ? []
    : [
        {
          title: "Market Segment",
          field: "market_segment",
          render: (rowData) => {
            return (rowData && rowData.market_segment) ? rowData.market_segment : "N/A";
          },
        },
      ];

  // Worksite column — where the agent captured the policy (OMM only,
  // additional_details.worksite_name). N/A for every other partner and for
  // policies onboarded before this field existed.
  const worksiteColumn = !isOmm
    ? []
    : [
        {
          title: "Worksite",
          field: "worksite_name",
          render: (rowData) => (rowData && rowData.worksite_name) ? rowData.worksite_name : "N/A",
        },
      ];

  // Other columns
  const otherColumns = [
    {
      title: "Principal",
      field: "principal",
      render: (rowData) => rowData.principal || "N/A",
    },
    {
      title: "Status",
      field: "status",
      render: (rowData) => rowData.status || "N/A",
    },
    {
      title: "Premium",
      field: "premium_granularity",
      render: (rowData) => rowData.premium_granularity || "N/A",
    },
    {
      title: "Created Date",
      field: "created_at",
      render: (rowData) => rowData.created_at || "N/A",
    },
  ];

  // AML column — a visible "AML FLAGGED" indicator so reviewers can spot
  // AML-flagged policies in the list. Opt-in (New Business tabs) via showAmlFlag.
  const amlColumn = props.showAmlFlag
    ? [
        {
          title: "AML",
          field: "aml_flagged",
          render: (rowData) => {
            const ad = rowData && rowData.additional_details;
            return (ad && ad.aml_flagged) ? (
              <span
                style={{
                  background: '#FEE2E2',
                  color: '#B91C1C',
                  fontWeight: 600,
                  fontSize: '11px',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  whiteSpace: 'nowrap',
                }}
              >
                AML FLAGGED
              </span>
            ) : null;
          },
        },
      ]
    : [];

  // Branch column — the agent's branch. Opt-in (Sales Manager view).
  const branchColumn = props.showBranch
    ? [
        {
          title: "Branch",
          field: "agent_branch",
          render: (rowData) =>
            (rowData && rowData.agent_branch) ? rowData.agent_branch : "N/A",
        },
      ]
    : [];

  // Approval Stage column — only shown when the parent opts in (Sales Manager,
  // maker-checker dashboards). Reads the current pending stage label straight
  // off the policy.
  const stageColumn = props.showStage
    ? [
        {
          title: "Approval Stage",
          field: "approval_stage",
          render: (rowData) => stageLabel(rowData),
        },
      ]
    : [];

  // Who decided this stage and when. Opt-in via decisionStage (the Approved
  // and Rejected tabs), which names the check to read — a policy carries one
  // per level.
  const decisionColumns = props.decisionStage
    ? [
        {
          title: "Decided By",
          field: "decided_by",
          render: (rowData) => {
            const check = getStageCheck(rowData, props.decisionStage);
            if (!check) {
              return "N/A";
            }
            return check.approver_full_name || check.approver_email || "N/A";
          },
        },
        {
          title: "Decided On",
          field: "decided_at",
          render: (rowData) => {
            const check = getStageCheck(rowData, props.decisionStage);
            return (check && formatDateLocale(check.decided_at)) || "N/A";
          },
        },
      ]
    : [];

  // Action column
  const actionColumns = [
    {
      title: "Actions",
      field: "actions",
      render: (rowData) => {
        return (
          <div>
            <ButtonInc
              onClick={() => hashHistory.push(`admin/policy-processing/${rowData.policy_number}`)}
              variant="contained"
              sx={{ textTransform: "none" }}
            >
              View Policy
            </ButtonInc>
          </div>
        );
      },
    },
  ];

  const columns = [...baseColumns, ...agentCodeColumn, ...agentEmailColumn, ...marketSegmentColumn, ...worksiteColumn, ...amlColumn, ...branchColumn, ...stageColumn, ...decisionColumns, ...otherColumns, ...actionColumns];

  // Views that narrow the server's result set client-side (need_action tabs,
  // maker-stage baskets) have no server-side page or total of their own, so
  // they fetch the whole matching set in one request (see the saga's fetchAll)
  // and let material-table paginate and count it locally.
  const useLocalPaging = !!props.needAction || !!props.localPaging;

  const handleTableChange = (newPage, pageSize) => {
    if (useLocalPaging) {
      return;
    }
    const payload = {
      page: newPage + 1,
      size: pageSize,
    };
    props.handleNextPage(payload);
  };

  const localization = props.emptyMessage
    ? { body: { emptyDataSourceMessage: props.emptyMessage } }
    : undefined;

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} md={12} sm={12}>
          {/* Server-side policy-number search, for views that supply a
              handler. Views without one hold their whole result set already,
              so they get material-table's own search instead (below). */}
          {props.onSearch ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                placeholder="Search by policy number"
                value={inputValue}
                onChange={function(e) { setInputValue(e.target.value) }}
                onKeyDown={function(e) { if (e.key === 'Enter') props.onSearch(inputValue) }}
                sx={{ minWidth: 240 }}
              />
              <ButtonInc
                variant="contained"
                onClick={function() { props.onSearch(inputValue) }}
                disabled={!inputValue}
                sx={{ textTransform: 'none' }}
              >
                Search
              </ButtonInc>
            </Box>
          ) : null}
          <TableInc
            columns={columns}
            data={tempArr}
            // Client-side-filtered views already have the whole matching set
            // in `tempArr` — letting material-table paginate/count it locally
            // (by omitting page/totalCount/onChangePage) gives an accurate
            // total for exactly this view. Other views keep the server-driven
            // paging they've always had.
            {...(useLocalPaging
              ? {}
              : {
                  page: (props.page || 1) - 1,
                  totalCount: props.total || 0,
                  onChangePage: handleTableChange,
                })}
            localization={localization}
            options={{
              pageSize: props.size || 10,
              paging: true,
              showTitle: false,
              search: !props.onSearch && useLocalPaging,
              emptyRowsWhenPaging: false,
            }}
          />
        </Grid>
      </Grid>
    </>
  );
};

export default DisplayRecentPolicies;